import { apiClient } from './client'

/**
 * Анкета ученика.
 *
 * Шаблонов анкет нет: системный набор полей один на платформу, учитель
 * настраивает его под себя — убирает лишние поля, делает необязательные
 * обязательными — и дополняет собственными полями.
 */

export type StudentFormFieldType =
  | 'text'
  | 'textarea'
  | 'select'
  | 'multiselect'
  | 'date'
  | 'phone'
  | 'email'
  | 'url'
  | 'boolean'
  | 'rating'
  | 'language_pair'

export type StudentFormFieldAccess = 'teacher_only' | 'student_visible' | 'student_editable'

/** Поле анкеты с применёнными настройками учителя. */
export interface StudentFormField {
  id: number
  key: string
  label: string
  type: StudentFormFieldType
  options?: string[] | null
  access: StudentFormFieldAccess
  sort_order: number
  /** Итоговая обязательность: системная либо включённая учителем. */
  required: boolean
  /** Учитель убрал поле из анкеты (в составе анкеты по договору таких нет). */
  hidden: boolean
  /** Поле можно убрать из анкеты. */
  can_hide: boolean
  /** Обязательность можно переключать. */
  can_toggle_required: boolean
  /** Собственное поле учителя — правится и удаляется целиком. */
  is_own: boolean
}

/** Собственное поле учителя: ключ, обязательность и удаляемость задаёт бэкенд. */
export interface StudentFormOwnFieldPayload {
  label: string
  type: StudentFormFieldType
  options?: string[]
  access?: StudentFormFieldAccess
}

/** Поле системного каталога — административный вид, без настроек учителя. */
export interface StudentFormCatalogField {
  id: number
  /** Генерируется из названия на бэкенде, руками не задаётся и не меняется. */
  key: string
  label: string
  type: StudentFormFieldType
  options?: string[] | null
  required: boolean
  deletable: boolean
  access: StudentFormFieldAccess
  sort_order: number
}

export interface StudentFormCatalogFieldPayload {
  label: string
  type: StudentFormFieldType
  options?: string[]
  required?: boolean
  deletable?: boolean
  access?: StudentFormFieldAccess
}

/** Значение зависит от типа поля: строка, список, булево, число или пара языков. */
export type StudentFormAnswerValue =
  | string
  | string[]
  | boolean
  | number
  | { from: string; to: string }
  | null

export interface StudentFormAnswer {
  field_key: string
  value: StudentFormAnswerValue
  updated_by_user_id?: number | null
  updated_at?: string | null
}

export interface StudentForm {
  id: number | null
  contract_id: number
  /** Заводилась ли анкета в БД. Поля приходят в любом случае. */
  exists: boolean
  fields: StudentFormField[]
  answers: StudentFormAnswer[]
  created_at?: string | null
  updated_at?: string | null
}

export interface StudentFormHistoryEntry {
  id: number
  field_key: string
  old_value: StudentFormAnswerValue
  new_value: StudentFormAnswerValue
  changed_by_user_id: number | null
  changed_at: string
}

interface ListResponse<T> {
  success?: boolean
  data: T[]
  meta?: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
}

interface ItemResponse<T> {
  success?: boolean
  message?: string
  data: T
}

export const studentFormsApi = {
  /** Состав анкеты у текущего учителя, включая скрытые им поля. */
  getFields: async (): Promise<StudentFormField[]> => {
    const response = await apiClient.get<ListResponse<StudentFormField>>('/student-form-fields')
    return response.data.data ?? []
  },

  /** Скрыть поле или переключить обязательность; возвращает весь состав анкеты. */
  updateFieldSetting: async (
    fieldKey: string,
    payload: { hidden?: boolean; required?: boolean },
  ): Promise<StudentFormField[]> => {
    const response = await apiClient.patch<ListResponse<StudentFormField>>(
      `/student-form-fields/${fieldKey}/settings`,
      payload,
    )
    return response.data.data ?? []
  },

  /** Завести собственное поле; в ответе — состав анкеты целиком. */
  createOwnField: async (payload: StudentFormOwnFieldPayload): Promise<StudentFormField[]> => {
    const response = await apiClient.post<ListResponse<StudentFormField>>(
      '/student-form-fields',
      payload,
    )
    return response.data.data ?? []
  },

  updateOwnField: async (
    id: number,
    payload: StudentFormOwnFieldPayload,
  ): Promise<StudentFormField[]> => {
    const response = await apiClient.patch<ListResponse<StudentFormField>>(
      `/student-form-fields/${id}`,
      payload,
    )
    return response.data.data ?? []
  },

  deleteOwnField: async (id: number): Promise<StudentFormField[]> => {
    const response = await apiClient.delete<ListResponse<StudentFormField>>(
      `/student-form-fields/${id}`,
    )
    return response.data.data ?? []
  },

  /** Анкета по договору. Поля приходят всегда, даже если анкету ещё не заполняли. */
  getForm: async (contractId: number): Promise<StudentForm> => {
    const response = await apiClient.get<ItemResponse<StudentForm>>(
      `/contracts/${contractId}/student-form`,
    )
    return response.data.data
  },

  /** Первое сохранение и заводит анкету — отдельного создания нет. */
  saveAnswers: async (
    contractId: number,
    answers: Record<string, StudentFormAnswerValue>,
  ): Promise<StudentForm> => {
    const response = await apiClient.patch<ItemResponse<StudentForm>>(
      `/contracts/${contractId}/student-form/answers`,
      { answers },
    )
    return response.data.data
  },

  getHistory: async (
    contractId: number,
    params?: { field_key?: string; per_page?: number; page?: number },
  ): Promise<{ data: StudentFormHistoryEntry[]; total: number }> => {
    const response = await apiClient.get<ListResponse<StudentFormHistoryEntry>>(
      `/contracts/${contractId}/student-form/history`,
      { params },
    )
    return {
      data: response.data.data ?? [],
      total: response.data.meta?.total ?? (response.data.data ?? []).length,
    }
  },

  /** Системный каталог полей — только админ. */
  admin: {
    list: async (): Promise<StudentFormCatalogField[]> => {
      const response = await apiClient.get<ListResponse<StudentFormCatalogField>>(
        '/admin/student-form-fields',
      )
      return response.data.data ?? []
    },

    create: async (payload: StudentFormCatalogFieldPayload): Promise<StudentFormCatalogField> => {
      const response = await apiClient.post<ItemResponse<StudentFormCatalogField>>(
        '/admin/student-form-fields',
        payload,
      )
      return response.data.data
    },

    update: async (
      id: number,
      payload: Partial<StudentFormCatalogFieldPayload>,
    ): Promise<StudentFormCatalogField> => {
      const response = await apiClient.patch<ItemResponse<StudentFormCatalogField>>(
        `/admin/student-form-fields/${id}`,
        payload,
      )
      return response.data.data
    },

    remove: async (id: number): Promise<void> => {
      await apiClient.delete(`/admin/student-form-fields/${id}`)
    },

    reorder: async (ids: number[]): Promise<StudentFormCatalogField[]> => {
      const response = await apiClient.post<ListResponse<StudentFormCatalogField>>(
        '/admin/student-form-fields/reorder',
        { ids },
      )
      return response.data.data ?? []
    },
  },
}
